import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { CheckCircle, MapPin, Phone } from 'lucide-react';
import { profileAPI } from '../../api/profile';
import { Card } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';

export default function EditProfilePage() {
  const [loading, setLoading]   = useState(true);
  const [success, setSuccess]   = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { address: '', additional_phone: '' } });

  // API #6 — pre-fill form with existing data
  useEffect(() => {
    profileAPI
      .getProfile()
      .then((res) => {
        const { address, additional_phone } = res.data.data;
        reset({
          address:          address          ?? '',
          additional_phone: additional_phone ?? '',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [reset]);

  // API #8 — submit updated details
  const onSubmit = async (data) => {
    setApiError('');
    try {
      await profileAPI.updateDetails({
        address:          data.address          || undefined,
        additional_phone: data.additional_phone || undefined,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setApiError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    }
  };

  return (
    <div>
      <PageHeader title="Edit Profile" subtitle="Update your address & contact info" showBack />

      <div className="px-4 py-4 pb-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              {/* Success banner */}
              {success && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-4 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2"
                >
                  <CheckCircle size={16} />
                  Profile details updated successfully!
                </motion.div>
              )}

              {/* Error banner */}
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600"
                >
                  {apiError}
                </motion.div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                {/* Address field */}
                <div className="flex flex-col gap-1">
                  <label className="input-label flex items-center gap-1.5">
                    <MapPin size={13} className="text-amber-500" />
                    Address
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Enter your full address"
                    className={`input-field resize-none ${errors.address ? 'border-red-400' : ''}`}
                    {...register('address', {
                      maxLength: { value: 500, message: 'Max 500 characters' },
                    })}
                  />
                  {errors.address && (
                    <p className="text-xs text-red-500 mt-0.5">{errors.address.message}</p>
                  )}
                </div>

                {/* Additional phone field */}
                <Input
                  label={
                    <span className="flex items-center gap-1.5">
                      <Phone size={13} className="text-amber-500" />
                      Alternate Phone Number
                    </span>
                  }
                  type="tel"
                  placeholder="10-digit mobile number"
                  error={errors.additional_phone?.message}
                  {...register('additional_phone', {
                    pattern: {
                      value:   /^\d{10}$/,
                      message: 'Enter a valid 10-digit number',
                    },
                  })}
                />

                <Button type="submit" loading={isSubmitting} fullWidth>
                  Save Changes
                </Button>
              </form>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
